import React from 'react'
import { Mail, Hourglass, ListChecks } from 'lucide-react'
import { cn } from '../UI/cn'
import { Tooltip } from '../Tooltip/tooltip'
import type { ActionSummaryStructuredData } from '../../services/recommendations_api'
import type { EnrichedAction } from '../../services/recommendations_contracts'

interface SummaryStatsHeaderProps {
  data: ActionSummaryStructuredData
  className?: string
  visibleActions?: EnrichedAction[]
  hiddenResolvedCount?: number
}

interface StatCardProps {
  icon: React.ReactNode
  value: number | string
  label: string
  tooltipTitle: string
  tooltipContent: string
}

const STAT_CARD_STYLE = 'bg-surface border-line text-fg-tertiary'

/**
 * Displays a single stat card with icon, value, label, and tooltip.
 *
 * @param icon - React node to display as the icon
 * @param value - Numeric or string value to display
 * @param label - Label text below the value
 * @param tooltipTitle - Title for the tooltip
 * @param tooltipContent - Content text for the tooltip
 */
const StatCard: React.FC<StatCardProps> = ({
  icon,
  value,
  label,
  tooltipTitle,
  tooltipContent,
}) => (
  <Tooltip
    title={tooltipTitle}
    content={tooltipContent}
    placement="top"
    delay={[300, 0]}
  >
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg border cursor-help',
        STAT_CARD_STYLE
      )}
    >
      <div className="flex-shrink-0 text-fg-secondary">{icon}</div>
      <div>
        <div className="text-lg font-bold leading-tight">{value}</div>
        <div className="text-xs opacity-80 leading-tight">{label}</div>
      </div>
    </div>
  </Tooltip>
)

/**
 * Summary stats header showing signal-based counts.
 *
 * Displays:
 * - Unreplied emails count (3-60 days old)
 * - Inactive deals count (7-90 days stale or 14+ days in stage)
 * - Total actions count
 */
export const SummaryStatsHeader: React.FC<SummaryStatsHeaderProps> = ({
  data,
  className,
  visibleActions,
  hiddenResolvedCount = 0,
}) => {
  const visibleActionsCount =
    visibleActions?.length ?? data.enriched_actions?.length ?? 0
  const unrepliedEmails = visibleActions
    ? visibleActions.filter(
        (action) => action.signal_type === 'unreplied_email'
      ).length
    : (data.signal_counts?.unreplied_email ??
      data.actions_by_signal?.unreplied_email?.length ??
      data.emails_needing_reply?.length ??
      0)
  const inactiveDeals = visibleActions
    ? visibleActions.filter((action) => action.signal_type === 'inactive_deal')
        .length
    : (data.signal_counts?.inactive_deal ??
      data.actions_by_signal?.inactive_deal?.length ??
      data.inactive_deals?.length ??
      0)
  const totalSignals =
    data.signal_counts?.total ?? unrepliedEmails + inactiveDeals

  const totalRecommendedActions =
    data.enriched_actions?.length ?? visibleActionsCount

  // Check if we're showing a subset of signals
  const hasMoreSignals =
    totalSignals > totalRecommendedActions && totalRecommendedActions > 0
  const hasHiddenResolved = hiddenResolvedCount > 0

  return (
    <div className={cn('space-y-2', className)}>
      <div className="grid grid-cols-3 gap-2">
        <StatCard
          icon={<Mail size={20} />}
          value={unrepliedEmails}
          label="Unreplied"
          tooltipTitle="Unreplied Emails"
          tooltipContent="Inbound emails from 3-60 days ago that need a response. Automated emails (shipping, out-of-office) are filtered out."
        />
        <StatCard
          icon={<Hourglass size={20} />}
          value={inactiveDeals}
          label="Inactive Deals"
          tooltipTitle="Inactive Deals"
          tooltipContent="Deals needing attention: 7+ days with no activity OR 14+ days stuck in the same stage. Limited to deals active within the last 90 days."
        />
        <StatCard
          icon={<ListChecks size={20} />}
          value={visibleActionsCount}
          label="Active"
          tooltipTitle="Recommended Actions"
          tooltipContent={`Showing ${visibleActionsCount} active action${visibleActionsCount === 1 ? '' : 's'}${hasHiddenResolved ? ` with ${hiddenResolvedCount} resolved hidden` : ''}.`}
        />
      </div>
      {(hasMoreSignals || hasHiddenResolved) && (
        <div className="flex items-center gap-2 text-xs text-fg-secondary rounded-md px-3 py-1.5">
          {hasHiddenResolved && (
            <span className="font-medium text-fg-secondary">
              {hiddenResolvedCount} resolved hidden from your active queue
            </span>
          )}
          {hasHiddenResolved && hasMoreSignals && (
            <span className="text-fg-muted">•</span>
          )}
          {hasMoreSignals && (
            <span>
              Showing top {totalRecommendedActions} of {totalSignals} detected
              signals
            </span>
          )}
        </div>
      )}
    </div>
  )
}
